import React, { useEffect, useState } from 'react';
import './Approvisionnement.css';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const API = window.location.hostname === 'localhost'
  ? 'http://localhost:5000/api'
  : 'https://myssa1.osc-fr1.scalingo.io/api';

// ★ RÉGLAGES TABLEAU — position et dimensions
const TABLE_CONFIG = {
  maxWidth:   '1800px',
  maxHeight:   '600px',
  marginTop:    '80px',
  marginLeft:  '-40px',
};

// ★ RÉGLAGES LARGEURS COLONNES — ajuste les valeurs px selon tes besoins
const COLUMN_WIDTHS = {
  'Fournisseur':             '200px',
  'Numero de commande':      '140px',
  'Numero de ligne':          '90px',
  'Item':                    '160px',
  'Description':             '250px',
  'Date de commande':        '120px',
  'Date de livraison':       '130px',
  'Quantite commandee':      '120px',
  'Quantité commandée':      '120px',
  'Quantite livree':         '110px',
  'Quantité livrée':         '110px',
  'RAL':                      '70px',
  'Statut AR':                '90px',
  'Approvisionneur':         '160px',
  'Horizon':                  '90px',
  'Montant':                 '110px',
  'Age':                      '70px',
};
const DEFAULT_WIDTH = '120px';

const calcTotalWidth = (cols) =>
  cols.reduce((s, c) => s + parseInt(COLUMN_WIDTHS[c] || DEFAULT_WIDTH, 10), 0);

const HIDDEN_COLUMNS = ['ID', 'Client_id'];

const Fournisseur = () => {
  const [donnees, setDonnees] = useState([]);
  const [filtres, setFiltres] = useState({ fournisseur: '', item: '', approvisionneur: '', numeroCommande: '' });

  useEffect(() => {
    document.title = 'MYSSA – Cmde fournisseur';
  }, []);

  useEffect(() => {
    fetch(`${API}/base_de_donnee_cmde_frn`)
      .then(r => r.json())
      .then(d => setDonnees(d))
      .catch(err => console.error(err));
  }, []);

  const handleReset = () =>
    setFiltres({ fournisseur: '', item: '', approvisionneur: '', numeroCommande: '' });

  const columns = donnees.length > 0
    ? Object.keys(donnees[0]).filter(c => !HIDDEN_COLUMNS.includes(c))
    : [];

  const filtered = donnees.filter(l =>
    l['Fournisseur']?.toLowerCase().includes(filtres.fournisseur) &&
    l['Item']?.toLowerCase().includes(filtres.item) &&
    l['Approvisionneur']?.toLowerCase().includes(filtres.approvisionneur) &&
    l['Numero de commande']?.toLowerCase().includes(filtres.numeroCommande)
  );

  const exportToExcel = () => {
    const ws  = XLSX.utils.json_to_sheet(filtered.map(r => ({ ...r })));
    const wb  = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Fournisseur');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `fournisseur_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const inputStyle = {
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '12px',
    padding: '6px 10px',
    border: '1px solid #e2d9cc',
    borderRadius: '6px',
    outline: 'none',
    background: '#fdfcfa',
    color: '#1e293b',
    width: '160px',
    height: '34px',
    boxSizing: 'border-box',
  };

  const btnStyle = {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '11px',
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
    border: '1px solid #d4c9b8',
    borderRadius: '6px',
    background: 'linear-gradient(180deg, #04202d, #38bdf8)',
    color: 'white',
    cursor: 'pointer',
    width: '160px',
    height: '34px',
    boxSizing: 'border-box',
    padding: '0',
    flexShrink: 0,
  };

  const btnExportStyle = {
    ...btnStyle,
    background: '#ffffff',
    color: '#04202d',
    border: '1px solid #d4c9b8',
  };

  const totalWidth = calcTotalWidth(columns);

  return (
    <div style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>

      {/* Bandeau titre */}
      <div style={{ position: 'absolute', top: '25px', left: '160px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '3px', height: '18px', background: 'linear-gradient(180deg, #04202d, #38bdf8)', borderRadius: '2px' }} />
        <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '20px', fontWeight: '200', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#04202d' }}>
          Commandes fournisseurs
        </span>
      </div>

      {/* Filtres */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '80px', marginLeft: '-20px', marginBottom: '12px' }}>
        {[
          { name: 'fournisseur',     placeholder: 'Fournisseur…' },
          { name: 'item',            placeholder: 'Item…' },
          { name: 'approvisionneur', placeholder: 'Approvisionneur…' },
          { name: 'numeroCommande',  placeholder: 'N° commande…' },
        ].map(({ name, placeholder }) => (
          <input key={name} style={inputStyle} placeholder={placeholder}
            value={filtres[name]}
            onChange={e => setFiltres({ ...filtres, [name]: e.target.value.toLowerCase() })} />
        ))}
        <button style={btnStyle} onClick={handleReset}>Réinitialiser</button>
        <button style={btnExportStyle} onClick={exportToExcel}>Export Excel</button>
      </div>

      {/* Tableau */}
      <div
        className="table-container1"
        style={{
          marginLeft: TABLE_CONFIG.marginLeft,
          maxWidth:   TABLE_CONFIG.maxWidth,
          maxHeight:  TABLE_CONFIG.maxHeight,
          marginTop:  TABLE_CONFIG.marginTop,
        }}
      >
        <table style={{ tableLayout: 'fixed', width: `${totalWidth}px` }}>
          <colgroup>
            {columns.map(c => <col key={c} width={COLUMN_WIDTHS[c] || DEFAULT_WIDTH} />)}
          </colgroup>
          <thead>
            <tr>{columns.map(c => <th key={c}>{c}</th>)}</tr>
          </thead>
          <tbody>
            {filtered.map((ligne, i) => (
              <tr key={i}>
                {columns.map((c, j) => (
                  <td key={j} style={c === 'Statut AR' ? {
                    backgroundColor: ligne[c]?.toUpperCase() === 'OUI' ? '#EAF8F1' : ligne[c]?.toUpperCase() === 'NON' ? '#FCEDEB' : 'transparent',
                    color:           ligne[c]?.toUpperCase() === 'OUI' ? '#1E8E5A' : ligne[c]?.toUpperCase() === 'NON' ? '#C0392B' : 'inherit',
                    fontWeight: 600,
                  } : {}}>
                    {ligne[c]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Fournisseur;